import React, { useState, useMemo, useEffect } from 'react';
import { supabase } from '../lib/supabase';
import { 
  Calculator, 
  TrendingUp, 
  CreditCard, 
  Users, 
  DollarSign, 
  Receipt,
  Search,
  Plus,
  ChevronDown,
  ChevronRight,
  BarChart3,
  Settings,
  AlertCircle,
  CheckCircle,
  Info,
  Download,
  Upload,
  Activity
} from 'lucide-react';

const ChartOfAccountsView = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('ALL');
  const [expandedAccounts, setExpandedAccounts] = useState(new Set(['1000', '1100', '1200', '1300', '1400', '1500', '2000', '2100', '2200', '2300', '2400', '2500', '3000', '4000', '5000', '5100', '5200', '5300', '5400', '5500', '5600', '5700']));
  const [selectedAccount, setSelectedAccount] = useState(null);
  const [viewMode, setViewMode] = useState('tree'); // tree, list, summary
  const [showInactive, setShowInactive] = useState(false);
  const [chartOfAccounts, setChartOfAccounts] = useState([]);
  const [accountCategories, setAccountCategories] = useState([]);
  const [accountTransactions, setAccountTransactions] = useState([]);
  const [accountBalancesSummary, setAccountBalancesSummary] = useState({});
  const [loading, setLoading] = useState(true);
  const [journalLinesData, setJournalLinesData] = useState([]);

  // Calculate account balance from journal lines
  const calculateAccountBalance = (accountCode, accountType) => {
    const lines = journalLinesData?.filter(line => line.account_code === accountCode) || [];
    const debits = lines.reduce((sum, line) => sum + (parseFloat(line.debit_amount) || 0), 0);
    const credits = lines.reduce((sum, line) => sum + (parseFloat(line.credit_amount) || 0), 0);
    
    // Assets and Expenses have debit balances, Liabilities/Equity/Revenue have credit balances
    if (accountType === 'Asset' || accountType === 'Expense') {
      return debits - credits;
    } else {
      return credits - debits;
    }
  };

  // Fetch data from Supabase
  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      
      // Fetch chart of accounts
      const { data: accountsData, error: accountsError } = await supabase
        .from('chart_of_accounts')
        .select('*')
        .order('account_code', { ascending: true });

      if (accountsError) throw accountsError;

      // Fetch account categories
      const { data: categoriesData, error: categoriesError } = await supabase
        .from('account_categories')
        .select('*')
        .order('sort_order', { ascending: true });

      if (categoriesError) throw categoriesError;

      // Fetch account transactions and journal lines
      const { data: transactionsData, error: transactionsError } = await supabase
        .from('financial_journal_entries')
        .select('*')
        .order('created_at', { ascending: false });

      if (transactionsError) throw transactionsError;

      // Fetch journal lines to calculate account balances
      const { data: journalLinesData, error: journalLinesError } = await supabase
        .from('financial_journal_lines')
        .select('*');

      if (journalLinesError) throw journalLinesError;

      setChartOfAccounts(accountsData || []);
      setAccountCategories(categoriesData || []);
      setAccountTransactions(transactionsData || []);
      setJournalLinesData(journalLinesData || []);

      // Calculate account balances summary
      const summary = {
        total_assets: 0,
        total_liabilities: 0,
        total_equity: 0,
        total_revenue: 0,
        total_expenses: 0,
        balance_check: true
      };

      accountsData?.forEach(account => {
        const balance = calculateAccountBalance(account.account_code, account.account_type);
        switch (account.account_type) {
          case 'Asset':
            summary.total_assets += balance;
            break;
          case 'Liability':
            summary.total_liabilities += balance;
            break;
          case 'Equity':
            summary.total_equity += balance;
            break;
          case 'Revenue':
            summary.total_revenue += balance;
            break;
          case 'Expense':
            summary.total_expenses += balance;
            break;
        }
      });

      summary.net_income = summary.total_revenue - summary.total_expenses;
      summary.balance_check = Math.abs(summary.total_assets - (summary.total_liabilities + summary.total_equity)) < 0.01;
      summary.last_updated = new Date().toISOString();
      setAccountBalancesSummary(summary);

    } catch (error) {
      console.error('Error fetching chart of accounts data:', error);
    } finally {
      setLoading(false);
    }
  };

  // Icon mapping for categories
  const categoryIcons = {
    'CAT_ASSET_CURRENT': TrendingUp,
    'CAT_ASSET_FIXED': CreditCard,
    'CAT_ASSET_INTANGIBLE': Users,
    'CAT_ASSET_OTHER': DollarSign,
    'CAT_LIABILITY_CURRENT': Receipt,
    'CAT_LIABILITY_LONG_TERM': CreditCard,
    'CAT_EQUITY_CAPITAL': Users,
    'CAT_REVENUE_SALES': TrendingUp,
    'CAT_REVENUE_SERVICES': Receipt,
    'CAT_REVENUE_OTHER': DollarSign,
    'CAT_EXPENSE_COGS': Calculator,
    'CAT_EXPENSE_OPERATING': BarChart3,
    'CAT_EXPENSE_TAX': Settings
  };

  // Toggle account expansion
  const toggleAccountExpansion = (accountCode) => {
    const newExpanded = new Set(expandedAccounts);
    if (newExpanded.has(accountCode)) {
      newExpanded.delete(accountCode);
    } else {
      newExpanded.add(accountCode);
    }
    setExpandedAccounts(newExpanded);
  };

  // Filter accounts based on search and category
  const filteredAccounts = useMemo(() => {
    return chartOfAccounts.filter(account => {
      const matchesSearch = account.account_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                           account.account_code?.includes(searchTerm) ||
                           account.description?.toLowerCase().includes(searchTerm.toLowerCase());
      
      const matchesCategory = selectedCategory === 'ALL' || account.account_type === selectedCategory;
      const matchesActive = showInactive || account.is_active;
      
      return matchesSearch && matchesCategory && matchesActive;
    });
  }, [chartOfAccounts, searchTerm, selectedCategory, showInactive]);

  // Get account hierarchy for tree view
  const getAccountHierarchy = () => {
    const accounts = filteredAccounts.sort((a, b) => (a.account_code || '').localeCompare(b.account_code || ''));
    const hierarchy = [];
    const accountMap = new Map();

    // Create account map
    accounts.forEach(account => {
      accountMap.set(account.account_code, { ...account, children: [] });
    });

    // Build hierarchy
    accounts.forEach(account => {
      if (account.parent_account && accountMap.has(account.parent_account)) {
        accountMap.get(account.parent_account).children.push(accountMap.get(account.account_code));
      } else {
        hierarchy.push(accountMap.get(account.account_code));
      }
    });

    return hierarchy;
  };

  // Format currency
  const formatCurrency = (amount) => {
    if (amount === undefined || amount === null || isNaN(amount)) return 'HK$0.00';
    return `HK$${parseFloat(amount).toLocaleString('en-HK', { minimumFractionDigits: 2 })}`;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-lg">Loading chart of accounts...</div>
      </div>
    );
  }

  // Render account tree item
  const renderAccountTreeItem = (account, level = 0) => {
    const hasChildren = account.children && account.children.length > 0;
    const isExpanded = expandedAccounts.has(account.account_code);
    const indentStyle = { paddingLeft: `${level * 24 + 16}px` };

    return (
      <div key={account.account_id} className="border-b border-gray-100">
        <div 
          className={`py-3 hover:bg-gray-50 transition-colors cursor-pointer ${
            selectedAccount?.account_id === account.account_id ? 'bg-blue-50 border-l-4 border-l-blue-500' : ''
          }`}
          style={indentStyle}
          onClick={() => setSelectedAccount(account)}
        >
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              {hasChildren && (
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    toggleAccountExpansion(account.account_code);
                  }}
                  className="p-1 hover:bg-gray-200 rounded"
                >
                  {isExpanded ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
                </button>
              )}
              {!hasChildren && <div className="w-6"></div>}
              
              <div className="flex items-center space-x-2">
                <span className={`px-2 py-1 text-xs rounded font-mono ${
                  !account.parent_account ? 'bg-gray-200 text-gray-700' : 'bg-blue-100 text-blue-700'
                }`}>
                  {account.account_code}
                </span>
                <div>
                  <p className={`font-medium ${
                    !account.parent_account ? 'text-gray-800 text-lg' : 'text-gray-700'
                  }`}>
                    {account.account_name}
                  </p>
                  <p className="text-sm text-gray-500">{account.description}</p>
                </div>
              </div>
            </div>
            
            <div className="flex items-center space-x-4">
              <div className="text-right">
                <p className="font-semibold text-blue-600">
                  {formatCurrency(calculateAccountBalance(account.account_code, account.account_type))}
                </p>
                <p className="text-xs text-gray-500">{account.account_type}</p>
              </div>
              
              <div className="flex items-center space-x-1">
                {!account.is_active && (
                  <span className="w-2 h-2 bg-red-500 rounded-full" title="Inactive"></span>
                )}
              </div>
            </div>
          </div>
        </div>
        
        {hasChildren && isExpanded && (
          <div>
            {account.children.map(child => renderAccountTreeItem(child, level + 1))}
          </div>
        )}
      </div>
    );
  };

  // Render category summary cards
  const renderCategorySummaryCards = () => {
    if (accountCategories.length === 0) {
      return null;
    }
    
    // Define the 5 main account groups
    const mainGroups = [
      {
        id: 'ASSETS',
        name: '資產',
        name_en: 'Assets',
        description: 'All company assets including current, fixed, and intangible assets',
        icon: TrendingUp,
        color: '#10B981', // Green
        accountTypes: ['Asset'],
        backgroundImage: '1579621970563-ebec7560ff3e'
      },
      {
        id: 'LIABILITIES',
        name: '負債',
        name_en: 'Liabilities',
        description: 'All company obligations including current and long-term liabilities',
        icon: CreditCard,
        color: '#EF4444', // Red
        accountTypes: ['Liability'],
        backgroundImage: '1554224154-26032ffc0d07'
      },
      {
        id: 'EQUITY',
        name: '股東權益',
        name_en: 'Shareholders Equity',
        description: 'Owners investment and retained earnings',
        icon: Users,
        color: '#3B82F6', // Blue
        accountTypes: ['Equity'],
        backgroundImage: '1521737711867-e3b97375f902'
      },
      {
        id: 'REVENUE',
        name: '收入',
        name_en: 'Revenue',
        description: 'All income sources including sales, services, and other revenue',
        icon: DollarSign,
        color: '#8B5CF6', // Purple
        accountTypes: ['Revenue'],
        backgroundImage: '1556742049-0cfed4f6a45d'
      },
      {
        id: 'EXPENSES',
        name: '費用',
        name_en: 'Expenses',
        description: 'All costs including COGS, operating expenses, and taxes',
        icon: Calculator,
        color: '#F59E0B', // Orange
        accountTypes: ['Expense'],
        backgroundImage: '1507679799987-c73779587ccf'
      }
    ];
    
    return (
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-6 mb-8">
        {mainGroups.map((group) => {
          const GroupIcon = group.icon;
          const groupAccounts = chartOfAccounts.filter(acc => group.accountTypes.includes(acc.account_type));
          const totalBalance = groupAccounts.reduce((sum, acc) => sum + calculateAccountBalance(acc.account_code, acc.account_type), 0);
          
          // Get subcategory breakdown for tooltip
          const subcategories = accountCategories.filter(cat => {
            switch (group.id) {
              case 'ASSETS':
                return cat.category_id.includes('ASSET');
              case 'LIABILITIES':
                return cat.category_id.includes('LIABILITY');
              case 'EQUITY':
                return cat.category_id === 'CAT_EQUITY_CAPITAL';
              case 'REVENUE':
                return cat.category_id.includes('REVENUE');
              case 'EXPENSES':
                return cat.category_id.includes('EXPENSE');
              default:
                return false;
            }
          });
          
          return (
            <div 
              key={group.id}
              className="bg-white rounded-lg shadow-lg overflow-hidden hover:shadow-xl transition-all duration-300 transform hover:scale-105 cursor-pointer group"
              onClick={() => setSelectedCategory(selectedCategory === group.accountTypes[0] ? 'ALL' : group.accountTypes[0])}
            >
              <div 
                className="h-40 bg-gradient-to-br from-gray-100 to-gray-200 relative overflow-hidden"
                style={{
                  backgroundImage: `url(https://images.unsplash.com/photo-${group.backgroundImage}?w=400&h=200&fit=crop)`,
                  backgroundSize: 'cover',
                  backgroundPosition: 'center'
                }}
              >
                <div className="absolute inset-0 bg-gradient-to-br from-black/30 to-black/50 group-hover:from-black/20 group-hover:to-black/40 transition-all duration-300"></div>
                <div className="absolute top-4 left-4">
                  <GroupIcon 
                    className="w-10 h-10 text-white drop-shadow-lg" 
                    style={{ color: group.color }}
                  />
                </div>
                <div className="absolute top-4 right-4">
                  <span className="px-3 py-1 bg-white bg-opacity-20 rounded-full text-sm font-semibold text-white">
                    {groupAccounts.length}
                  </span>
                </div>
                <div className="absolute bottom-4 left-4 right-4">
                  <h3 className="text-xl font-bold text-white mb-1">{group.name}</h3>
                  <p className="text-sm text-white opacity-90">{group.name_en}</p>
                </div>
              </div>
              
              <div className="p-6">
                <p className="text-sm text-gray-600 mb-4 leading-relaxed">{group.description}</p>
                
                <div className="space-y-3">
                  <div className="text-center">
                    <p className="text-2xl font-bold text-gray-800 mb-1">
                      {formatCurrency(totalBalance)}
                    </p>
                    <p className="text-xs text-gray-500">Total Balance</p>
                  </div>
                  
                  {/* Subcategory breakdown */}
                  <div className="border-t pt-3">
                    <p className="text-xs font-medium text-gray-700 mb-2">Subcategories:</p>
                    <div className="space-y-1">
                      {subcategories.slice(0, 3).map((subcat) => {
                        const subcatAccounts = chartOfAccounts.filter(acc => {
                          switch (group.id) {
                            case 'ASSETS':
                              return acc.account_type === 'Asset' && acc.account_code.startsWith(subcat.category_id.replace('CAT_ASSET_', ''));
                            case 'LIABILITIES':
                              return acc.account_type === 'Liability' && acc.account_code.startsWith(subcat.category_id.replace('CAT_LIABILITY_', ''));
                            case 'EQUITY':
                              return acc.account_type === 'Equity';
                            case 'REVENUE':
                              return acc.account_type === 'Revenue' && acc.account_code.startsWith(subcat.category_id.replace('CAT_REVENUE_', ''));
                            case 'EXPENSES':
                              return acc.account_type === 'Expense' && acc.account_code.startsWith(subcat.category_id.replace('CAT_EXPENSE_', ''));
                            default:
                              return false;
                          }
                        });
                        const subcatBalance = subcatAccounts.reduce((sum, acc) => sum + calculateAccountBalance(acc.account_code, acc.account_type), 0);
                        
                        return (
                          <div key={subcat.category_id} className="flex justify-between items-center text-xs">
                            <span className="text-gray-600 truncate">{subcat.category_name_en}</span>
                            <span className="font-medium text-gray-800">{formatCurrency(subcatBalance)}</span>
                          </div>
                        );
                      })}
                      {subcategories.length > 3 && (
                        <div className="text-xs text-gray-500 text-center pt-1">
                          +{subcategories.length - 3} more
                        </div>
                      )}
                    </div>
                  </div>
                </div>
                
                {/* Selection indicator */}
                <div className={`mt-4 p-2 rounded-lg text-center text-sm font-medium transition-all duration-200 ${
                  selectedCategory === group.accountTypes[0] 
                    ? 'bg-blue-100 text-blue-800 border-2 border-blue-200' 
                    : 'bg-gray-50 text-gray-600 hover:bg-gray-100'
                }`}>
                  {selectedCategory === group.accountTypes[0] ? 'Selected' : 'Click to filter'}
                </div>
              </div>
            </div>
          );
        })}
      </div>
    );
  };

  // Render account details panel
  const renderAccountDetails = () => {
    if (!selectedAccount) {
      return (
        <div className="bg-white rounded-lg shadow-lg p-8 text-center">
          <Calculator className="w-16 h-16 text-gray-300 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-500 mb-2">Select an Account</h3>
          <p className="text-gray-400">Choose an account from the chart to view details</p>
        </div>
      );
    }

    const categoryInfo = accountCategories.find(cat => {
      // Map account type to category
      switch (selectedAccount.account_type) {
        case 'Asset':
          return cat.category_id.includes('ASSET');
        case 'Liability':
          return cat.category_id.includes('LIABILITY');
        case 'Equity':
          return cat.category_id === 'CAT_EQUITY_CAPITAL';
        case 'Revenue':
          return cat.category_id.includes('REVENUE');
        case 'Expense':
          return cat.category_id.includes('EXPENSE');
        default:
          return false;
      }
    });

    const accountTransactionsList = accountTransactions.filter(txn => txn.account_id === selectedAccount.account_id);

    return (
      <div className="bg-white rounded-lg shadow-lg overflow-hidden">
        {/* Account Header */}
        <div 
          className="h-48 bg-gradient-to-br from-gray-100 to-gray-200 relative overflow-hidden"
          style={{
            backgroundImage: `url(https://images.unsplash.com/photo-${selectedAccount.account_type === 'Asset' ? '1565628966040-f0e5b17e74b3' : 
              selectedAccount.account_type === 'Liability' ? '1560472354-b33ff0c44a43' :
              selectedAccount.account_type === 'Equity' ? '1559757148-5c350d2e24f9' :
              selectedAccount.account_type === 'Revenue' ? '1579621970563-ebec7560ff3e' :
              '1554224154-26032ffc0d07'}?w=800&h=300&fit=crop)`,
            backgroundSize: 'cover',
            backgroundPosition: 'center'
          }}
        >
          <div className="absolute inset-0 bg-gradient-to-r from-black/60 to-black/30"></div>
          <div className="absolute bottom-0 left-0 right-0 p-6 text-white">
            <div className="flex items-center space-x-3 mb-2">
              <span className="px-3 py-1 bg-white bg-opacity-20 rounded-full text-sm font-mono">
                {selectedAccount.account_code}
              </span>
              <span className={`px-3 py-1 rounded-full text-sm ${
                !selectedAccount.parent_account ? 'bg-yellow-500 bg-opacity-80' : 'bg-blue-500 bg-opacity-80'
              }`}>
                {selectedAccount.account_type}
              </span>
            </div>
            <h2 className="text-2xl font-bold mb-1">{selectedAccount.account_name}</h2>
            <p className="text-gray-200">{selectedAccount.description}</p>
          </div>
        </div>

        <div className="p-6">
          {/* Account Information Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-6">
            <div className="bg-gray-50 rounded-lg p-4">
              <div className="flex items-center space-x-2 mb-2">
                <Info className="w-5 h-5 text-blue-600" />
                <h4 className="font-medium text-gray-800">Basic Information</h4>
              </div>
              <div className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <span className="text-gray-600">Category:</span>
                  <span className="font-medium" style={{ color: categoryInfo?.color_code }}>
                    {categoryInfo?.category_name || selectedAccount.account_type}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Account Type:</span>
                  <span className="font-medium text-blue-600">
                    {selectedAccount.account_type}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Parent Account:</span>
                  <span className="font-medium">
                    {selectedAccount.parent_account || 'None'}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Status:</span>
                  <span className={`flex items-center space-x-1 ${selectedAccount.is_active ? 'text-green-600' : 'text-red-600'}`}>
                    {selectedAccount.is_active ? <CheckCircle className="w-4 h-4" /> : <AlertCircle className="w-4 h-4" />}
                    <span className="font-medium">{selectedAccount.is_active ? 'Active' : 'Inactive'}</span>
                  </span>
                </div>
              </div>
            </div>

            <div className="bg-gray-50 rounded-lg p-4">
              <div className="flex items-center space-x-2 mb-2">
                <BarChart3 className="w-5 h-5 text-green-600" />
                <h4 className="font-medium text-gray-800">Balance Information</h4>
              </div>
              <div className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <span className="text-gray-600">Current Balance:</span>
                  <span className="font-bold text-lg text-gray-800">
                    {formatCurrency(calculateAccountBalance(selectedAccount.account_code, selectedAccount.account_type))}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Last Updated:</span>
                  <span className="font-medium">
                    {new Date(selectedAccount.updated_at || selectedAccount.created_at).toLocaleDateString('zh-TW')}
                  </span>
                </div>
              </div>
            </div>

            <div className="bg-gray-50 rounded-lg p-4">
              <div className="flex items-center space-x-2 mb-2">
                <Settings className="w-5 h-5 text-purple-600" />
                <h4 className="font-medium text-gray-800">System Properties</h4>
              </div>
              <div className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <span className="text-gray-600">Account Code:</span>
                  <span className="font-medium text-blue-600">
                    {selectedAccount.account_code}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Account ID:</span>
                  <span className="font-medium text-gray-600">
                    {selectedAccount.account_id}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Created:</span>
                  <span className="font-medium">
                    {new Date(selectedAccount.created_at).toLocaleDateString('zh-TW')}
                  </span>
                </div>
              </div>
            </div>
          </div>

          {/* Account Description */}
          <div className="bg-blue-50 rounded-lg p-4 mb-6">
            <h4 className="font-medium text-gray-800 mb-2">Description</h4>
            <p className="text-gray-700">{selectedAccount.description}</p>
          </div>

          {/* Recent Transactions */}
          <div>
            <h4 className="font-medium text-gray-800 mb-4">Recent Transactions</h4>
            {accountTransactions.length > 0 ? (
              <div className="bg-gray-50 rounded-lg overflow-hidden">
                <div className="divide-y divide-gray-200">
                  {accountTransactions.slice(0, 5).map((transaction) => (
                    <div key={transaction.entry_id} className="p-4 hover:bg-gray-100 transition-colors">
                      <div className="flex justify-between items-start">
                        <div>
                          <p className="font-medium text-gray-800">{transaction.description}</p>
                          <p className="text-sm text-gray-500">
                            {new Date(transaction.entry_date || transaction.created_at).toLocaleDateString('zh-TW')} •
                            {transaction.entry_type}: {transaction.entry_number}
                          </p>
                        </div>
                        <div className="text-right">
                          {transaction.debit_amount > 0 && (
                            <p className="text-green-600 font-semibold">
                              Dr. {formatCurrency(transaction.debit_amount)}
                            </p>
                          )}
                          {transaction.credit_amount > 0 && (
                            <p className="text-blue-600 font-semibold">
                              Cr. {formatCurrency(transaction.credit_amount)}
                            </p>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ) : (
              <div className="text-center py-8 text-gray-500">
                <Activity className="w-12 h-12 mx-auto mb-2 text-gray-300" />
                <p>No recent transactions</p>
              </div>
            )}
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="space-y-6">
      {/* Professional Header with Unsplash Background */}
      <div 
        className="bg-gradient-to-r from-slate-900 to-slate-700 rounded-lg shadow-lg overflow-hidden"
        style={{
          backgroundImage: 'url(https://images.unsplash.com/photo-1554224154-26032ffc0d07?w=1200&h=400&fit=crop)',
          backgroundSize: 'cover',
          backgroundPosition: 'center'
        }}
      >
        <div className="bg-gradient-to-r from-black/80 to-black/40 p-8 text-white">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-4xl font-bold mb-3">Chart of Accounts</h1>
              <h2 className="text-2xl mb-2">會計科目表管理系統</h2>
              <p className="text-slate-300 text-lg mb-4">Professional accounting structure management</p>
              
              {/* Balance Summary */}
              <div className="flex flex-wrap gap-6 text-sm">
                <div className="bg-white bg-opacity-10 px-4 py-2 rounded-lg">
                  <p className="text-slate-300">Total Assets</p>
                  <p className="text-xl font-bold">{formatCurrency(accountBalancesSummary.total_assets)}</p>
                </div>
                <div className="bg-white bg-opacity-10 px-4 py-2 rounded-lg">
                  <p className="text-slate-300">Total Liabilities</p>
                  <p className="text-xl font-bold">{formatCurrency(accountBalancesSummary.total_liabilities)}</p>
                </div>
                <div className="bg-white bg-opacity-10 px-4 py-2 rounded-lg">
                  <p className="text-slate-300">Total Equity</p>
                  <p className="text-xl font-bold">{formatCurrency(accountBalancesSummary.total_equity)}</p>
                </div>
                <div className="bg-white bg-opacity-10 px-4 py-2 rounded-lg">
                  <p className="text-slate-300">Net Income</p>
                  <p className="text-xl font-bold text-green-300">{formatCurrency(accountBalancesSummary.net_income)}</p>
                </div>
              </div>
            </div>
            
            <div className="text-right">
              <div className={`inline-flex items-center space-x-2 px-4 py-2 rounded-lg ${
                accountBalancesSummary.balance_check ? 'bg-green-500 bg-opacity-20 text-green-300' : 'bg-red-500 bg-opacity-20 text-red-300'
              }`}>
                {accountBalancesSummary.balance_check ? <CheckCircle className="w-5 h-5" /> : <AlertCircle className="w-5 h-5" />}
                <span className="font-medium">
                  {accountBalancesSummary.balance_check ? 'Balance Verified' : 'Balance Error'}
                </span>
              </div>
              <p className="text-sm text-slate-300 mt-2">
                Last Updated: {new Date(accountBalancesSummary.last_updated).toLocaleDateString('zh-TW')}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Category Summary Cards */}
      {renderCategorySummaryCards()}

      {/* Controls and Filters */}
      <div className="bg-white rounded-lg shadow-lg p-6">
        <div className="flex flex-wrap items-center justify-between gap-4">
          <div className="flex items-center space-x-4">
            <div className="relative">
              <Search className="w-5 h-5 absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
              <input
                type="text"
                placeholder="Search accounts..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent w-64"
              />
            </div>

            <select
              value={selectedCategory}
              onChange={(e) => setSelectedCategory(e.target.value)}
              className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            >
              <option value="ALL">All Account Groups</option>
              <option value="Asset">Assets (資產)</option>
              <option value="Liability">Liabilities (負債)</option>
              <option value="Equity">Equity (股東權益)</option>
              <option value="Revenue">Revenue (收入)</option>
              <option value="Expense">Expenses (費用)</option>
            </select>

            <div className="flex items-center space-x-2">
              <button
                onClick={() => setViewMode('tree')}
                className={`px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                  viewMode === 'tree' ? 'bg-blue-600 text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                Tree View
              </button>
              <button
                onClick={() => setViewMode('list')}
                className={`px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                  viewMode === 'list' ? 'bg-blue-600 text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                List View
              </button>
            </div>

            <label className="flex items-center space-x-2">
              <input
                type="checkbox"
                checked={showInactive}
                onChange={(e) => setShowInactive(e.target.checked)}
                className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
              />
              <span className="text-sm text-gray-700">Show Inactive</span>
            </label>
          </div>

          <div className="flex items-center space-x-2">
            <button className="flex items-center space-x-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors">
              <Plus className="w-4 h-4" />
              <span>Add Account</span>
            </button>
            <button className="flex items-center space-x-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors">
              <Download className="w-4 h-4" />
              <span>Export</span>
            </button>
            <button className="flex items-center space-x-2 px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors">
              <Upload className="w-4 h-4" />
              <span>Import</span>
            </button>
          </div>
        </div>
      </div>

      {/* Main Content Area */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Accounts List/Tree */}
        <div className="lg:col-span-2">
          <div className="bg-white rounded-lg shadow-lg">
            <div className="p-4 border-b border-gray-200">
              <h3 className="text-lg font-semibold text-gray-800">Accounts Structure</h3>
              <p className="text-sm text-gray-600">
                {filteredAccounts.length} accounts {selectedCategory !== 'ALL' && `in ${selectedCategory}`}
              </p>
            </div>
            
            <div className="max-h-96 overflow-y-auto">
              {viewMode === 'tree' ? (
                <div>
                  {getAccountHierarchy().map(account => renderAccountTreeItem(account))}
                </div>
              ) : (
                <div>
                  {filteredAccounts.map(account => renderAccountTreeItem(account, 0))}
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Account Details */}
        <div className="lg:col-span-1">
          {renderAccountDetails()}
        </div>
      </div>
    </div>
  );
};

export default ChartOfAccountsView; 